import { useCallback, useEffect, useState } from 'react'
import { CameraFill } from 'react-bootstrap-icons'
import { useDropzone } from 'react-dropzone'
import { ReactSortable } from 'react-sortablejs'

import classNames from 'classnames'
import heic2any from 'heic2any'

import { colors } from '../../../constants/salesStyles'
import { isArrayOfTypeFile } from '../../../pages/NewPart/partReducer'
import { Image, partInput } from '../../../types/part/partTypes'
import { rotateImageFile } from '../../../utils/rotateImageFile'
import ImageRotateActionsBar from '../../_atoms/ImageRotateActionsBar/ImageRotateActionsBar'
import Loader from '../../_atoms/Loader/Loader'
import ModalCarousel from '../../_atoms/ModalCarousel/ModalCarousel'
import OverlayWithText from '../../_atoms/OverlayWithText/OverlayWithText'
import SalesText from '../../_atoms/SalesText/SalesText'
import SalesThumbPreview from '../../_atoms/SalesThumbPreview/SalesThumbPreview'

import styles from './styles.module.scss'

interface IProps {
    input: partInput
    handleAddImages: (file: File[]) => void
    removeFile: (index: number) => void
    replaceFile: (index: number, file: File) => void
    handleImagesReorder: (images: File[]) => void
}

const SalesUploadImages = ({ input, handleAddImages, removeFile, replaceFile, handleImagesReorder }: IProps) => {
    const { value, specialProps } = input
    const fileTypes = {
        'image/jpg': ['.jpg'],
        'image/jpeg': ['.jpeg'],
        'image/png': ['.png'],
        'image/heic': ['.heic'],
    }

    const files = value
    const fileList = Array.isArray(files) && isArrayOfTypeFile(files) ? files : null
    const isShowPreviews = Boolean(fileList && fileList.length > 0)
    const isDisabled = Boolean(fileList && fileList.length >= 10)

    const [imagesToReorder, setImagesToReorder] = useState<Image[]>([])
    const [pendingRotations, setPendingRotations] = useState<Record<number, number>>({})
    const [isSavingRotations, setIsSavingRotations] = useState(false)

    const pendingCount = Object.keys(pendingRotations).length

    const handleImageReorder = () => {
        handleImagesReorder(imagesToReorder.map(image => image.file))
    }

    const onSortableChange = (sortedImages: Image[]) => {
        setImagesToReorder(sortedImages)
    }

    const [activeImageIndex, setActiveImageIndex] = useState(0)
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [isLoading, setIsLoading] = useState(false)

    const setActiveImage = (index: number) => {
        setActiveImageIndex(index)
        setIsModalOpen(true)
    }

    const handleRotate = (index: number) => {
        setPendingRotations(prev => {
            const currentAngle = prev[index] ?? 0
            const newAngle = (currentAngle + 90) % 360

            if (newAngle === 0) {
                const next = { ...prev }
                delete next[index]
                return next
            }

            return { ...prev, [index]: newAngle }
        })
    }

    const handleCancelRotations = () => {
        setPendingRotations({})
    }

    const handleSaveRotations = async () => {
        if (!fileList || pendingCount === 0) {
            return
        }

        setIsSavingRotations(true)
        try {
            const entries = Object.entries(pendingRotations)

            for (const [indexStr, angle] of entries) {
                const index = Number(indexStr)
                const file = fileList[index]
                if (!file || angle === 0) {
                    continue
                }

                const rotatedFile = await rotateImageFile(file, angle)
                replaceFile(index, rotatedFile)
            }

            setPendingRotations({})
        } catch (e) {
            console.error('rotate files error', e)
        } finally {
            setIsSavingRotations(false)
        }
    }

    const handleFileUpload = async (acceptedFiles: File[]) => {
        setIsLoading(true)
        for (const file of acceptedFiles) {
            let convertedFile = file
            if (file.type === 'image/heic') {
                const converted = await heic2any({ blob: file, toType: 'image/jpeg' })
                convertedFile = new File([converted as Blob], file.name.replace(/\..+$/, '.jpeg'), {
                    type: 'image/jpeg',
                })
            }
            handleAddImages([convertedFile])
        }
        setIsLoading(false)
    }

    useEffect(() => {
        if (fileList) {
            const sortableFiles = fileList.map((file, index) => ({
                id: file.name,
                file: file,
                index: index,
            }))
            setImagesToReorder(sortableFiles)
        }
    }, [fileList])

    useEffect(() => {
        setPendingRotations(prev => {
            if (!fileList) {
                return {}
            }

            const next: Record<number, number> = {}
            Object.entries(prev).forEach(([indexStr, angle]) => {
                const index = Number(indexStr)
                if (fileList[index] && angle > 0) {
                    next[index] = angle
                }
            })
            return next
        })
    }, [fileList])

    const onDrop = useCallback((acceptedFiles: File[]) => {
        handleFileUpload(acceptedFiles)
    }, [])

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: fileTypes,
        multiple: true,
        disabled: isDisabled || isLoading || isSavingRotations,
        onDropRejected: () => console.error('Формат файла не поддерживается.'),
    })

    return (
        <div className={styles.wrap}>
            {isDisabled ? (
                <div className={`${styles.dropAreaWrap} ${styles.disabled}`}>
                    <SalesText text={'Не более 10 фотографий'} />
                </div>
            ) : (
                <div
                    {...getRootProps({ className: classNames(styles.dropAreaWrap, { [styles.disabled]: isLoading }) })}
                >
                    <input {...getInputProps()} />
                    <SalesText text={specialProps?.renderValue || 'add button title to RENDER_VALUE'} />
                    <div className={styles.iconWrap}>
                        <CameraFill color={colors.blueDark} size={18} />
                    </div>
                </div>
            )}

            <>
                <div className={styles.thumbsWrap}>
                    {!isShowPreviews ? null : (
                        <ReactSortable
                            list={imagesToReorder}
                            setList={onSortableChange}
                            className={styles.flex}
                            onEnd={handleImageReorder}
                            disabled={isLoading || isSavingRotations}
                        >
                            {imagesToReorder.map((image, index) => (
                                <SalesThumbPreview
                                    key={image.id + index}
                                    index={index}
                                    file={image.file}
                                    previewAngle={pendingRotations[index] ?? 0}
                                    isSaving={isSavingRotations}
                                    removeFile={removeFile}
                                    onRotate={handleRotate}
                                    setActiveImageIndex={setActiveImage}
                                />
                            ))}
                        </ReactSortable>
                    )}
                </div>
                <ImageRotateActionsBar
                    count={pendingCount}
                    isSaving={isSavingRotations}
                    onSave={handleSaveRotations}
                    onCancel={handleCancelRotations}
                />
            </>
            {
                <ModalCarousel
                    closeModal={() => setIsModalOpen(false)}
                    pictures={imagesToReorder}
                    isOpen={isModalOpen}
                    pictureIndex={activeImageIndex}
                />
            }

            {isLoading && (
                <OverlayWithText backgroundBootstrapColor={'bg-secondary'}>
                    <Loader diameterInPx={90} thicknessInPx={9} />
                </OverlayWithText>
            )}
        </div>
    )
}

export default SalesUploadImages
